import React from "react";

export interface Step {
  title: string;
  status?: "completed" | "active" | "disabled";
  icon?: React.ReactNode;
}

interface StepperProps {
  steps: Step[];
  className?: string;
  onStepClick?: (index: number) => void;
  orientation?: "horizontal" | "vertical";
  activeClassName?: string;
  completedClassName?: string;
  disabledClassName?: string;
  /** Override for steps that are neither active, completed nor disabled. */
  inactiveClassName?: string;
  theme?: "light" | "dark";
  /**
   * Step button vertical density. `md` (default) keeps the current
   * `px-4 py-2 text-sm`. `sm` slims it down to `px-3 py-1 text-xs`.
   */
  size?: "sm" | "md";
  /**
   * Horizontal only. By default the steps stack below `md`, one per row.
   * `true` keeps them in one row and lets a long label wrap instead — for a
   * few short steps in a modal, where three stacked rows took a third of a
   * phone screen.
   */
  rowOnMobile?: boolean;
}

/** Check Icon */
function Check({ className = "" }: { className?: string }) {
  return (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M17.17 7.85012L10.49 14.5301L6.61 10.6501C6.42264 10.4639 6.16919 10.3593 5.905 10.3593C5.64082 10.3593 5.38737 10.4639 5.2 10.6501C5.01375 10.8375 4.90921 11.0909 4.90921 11.3551C4.90921 11.6193 5.01375 11.8728 5.2 12.0601L9.79 16.6501C9.97737 16.8364 10.2308 16.9409 10.495 16.9409C10.7592 16.9409 11.0126 16.8364 11.2 16.6501L18.58 9.27012C18.6737 9.17716 18.7481 9.06655 18.7989 8.94469C18.8497 8.82284 18.8758 8.69213 18.8758 8.56012C18.8758 8.42811 18.8497 8.2974 18.7989 8.17554C18.7481 8.05368 18.6737 7.94308 18.58 7.85012C18.3926 7.66387 18.1392 7.55933 17.875 7.55933C17.6108 7.55933 17.3574 7.66387 17.17 7.85012Z" fill="currentColor"/>
  </svg>
  );
}

export const GenericStepper: React.FC<StepperProps> = ({
  steps,
  className = "",
  onStepClick,
  orientation = "horizontal",
  theme = "light",
  size = "md",
  rowOnMobile = false,
  activeClassName,
  completedClassName,
  disabledClassName,
  inactiveClassName,
}) => {
  // Theme-based styles
  const baseBackground = theme === "dark" ? "bg-semantic-bg-brand-bold" : "bg-semantic-bg-secondary";
  const baseText = theme === "dark" ? "text-semantic-text-primary" : "text-semantic-text-secondary";
  const borderColor = theme === "dark" ? "border-semantic-bg-brand-bold" : "border-semantic-bg-bg-primary";
  const activeColor =
  theme === "dark"
    ? "bg-brand-bold-secondary text-white"
    : "bg-primary shadow-sm";
  const completedColor = theme === "dark" ? "text-semantic-text-secondary" : "text-semantic-text-primary";
  const disabledColor = theme === "dark" ? "text-semantic-text-secondary" : "text-semantic-text-secondary";

  // Per-status overrides — caller-supplied classNames fully replace the
  // defaults so Tailwind specificity wars don't leak the old styles.
  const resolvedActive = activeClassName ?? activeColor;
  const resolvedCompleted = completedClassName ?? completedColor;
  const resolvedDisabled = disabledClassName ?? disabledColor;
  const resolvedInactive = inactiveClassName ?? baseText;
  const sizeClass = size === "sm" ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm";

  // Layout strategy:
  //  - `vertical` orientation → always column-stacked (legacy behavior).
  //  - `horizontal` orientation → column on mobile (`<md`), row on `md+`.
  //    On mobile, cramming N labels into a single row with whitespace-nowrap
  //    overflows the container; stacking gives each step a full-width row.
  const containerOrientation =
    orientation === "vertical"
      ? "flex-col space-y-4"
      : rowOnMobile
        ? "flex-row"
        : "flex-col md:flex-row";

  // Each step is a flex item: full-width on mobile (stacked), evenly
  // distributed on `md+` (`flex-1` only kicks in when row layout returns).
  // In a row on mobile the labels may wrap, so a long one takes two lines
  // rather than pushing the row past its container.
  const itemSizing =
    orientation === "vertical"
      ? "whitespace-nowrap"
      : rowOnMobile
        ? "min-w-0 flex-1 whitespace-normal text-center leading-tight md:whitespace-nowrap"
        : "w-full whitespace-nowrap md:flex-1";

  return (
    <div
      className={`rounded-lg ${baseBackground} border ${borderColor} rounded-md flex ${containerOrientation} ${className}`}
    >
      {steps.map((step, index) => {
        const isCompleted = step.status === "completed";
        const isActive = step.status === "active";
        const isDisabled = step.status === "disabled";

        return (
          <button
            key={index}
            disabled={isDisabled}
            onClick={() => !isDisabled && onStepClick?.(index)}
            className={`
              ${itemSizing} flex items-center justify-center gap-2 ${sizeClass} rounded-md transition-colors duration-200
              ${isActive ? resolvedActive : ""}
              ${isCompleted && !isActive ? resolvedCompleted : ""}
              ${isDisabled ? resolvedDisabled : ""}
              ${!isActive && !isCompleted && !isDisabled ? resolvedInactive : ""}
              ${isDisabled ? "cursor-not-allowed" : "hover:bg-secondary"}
            `}
          >
            {/* Show Check Icon for Completed Steps */}
            {isCompleted && !isActive && (
              <Check className="h-4 w-4 text-semantic-text-secondary" />
            )}

            {/* Step Title Only */}
            <span className="font-medium">{step.title}</span>
          </button>
        );
      })}
    </div>
  );
};

export default GenericStepper;
